import { useEffect, useState } from "react";
import { useSession } from "@/services/auth0/session";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { PaymentMethod, PaymentStyle } from "@/api.types";
import {
  useDeletePaymentMethod,
  useMakePaymentMethodDefault,
  usePaymentMethods,
} from "@/api/use-payment-methods-api";
import { AddPaymentMethodDialog } from "./add-payment-method.dialog";

interface PaymentStyleInfo {
  label: string;
  description: string;
}

const getPaymentStyleInfo = (style: PaymentStyle): PaymentStyleInfo => {
  switch (style) {
    case PaymentStyle.MANUAL:
      return {
        label: "Invoice Created Manually",
        description: "We will manually initiate payments for your orders.",
      };
    case PaymentStyle.PAY_PER_ORDER:
      return {
        label: "Pay Per Order",
        description:
          "Your payment method will be charged automatically after each order is placed.",
      };
    case PaymentStyle.MONTHLY_FIRST:
      return {
        label: "Pay Monthly (1st)",
        description:
          "All orders will be accumulated and charged automatically on the 1st of each month.",
      };
    case PaymentStyle.WEEKLY_SUNDAY:
      return {
        label: "Pay Weekly (Sunday)",
        description:
          "All orders will be accumulated and charged automatically every Sunday.",
      };
    default:
      return {
        label: style,
        description:
          "Contact support for more information about this payment style.",
      };
  }
};

const getCardBrandLogo = (brand?: string): string => {
  const baseUrl =
    "https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards";
  switch (brand?.toLowerCase()) {
    case "mastercard":
      return `${baseUrl}/mastercard.svg`;
    case "visa":
      return `${baseUrl}/visa.svg`;
    case "amex":
    case "american express":
      return `${baseUrl}/american-express.svg`;
    case "diners":
    case "diners club":
      return `${baseUrl}/diners.svg`;
    case "discover":
      return `${baseUrl}/discover.svg`;
    case "jcb":
      return `${baseUrl}/jcb.svg`;
    case "unionpay":
      return `${baseUrl}/unionpay.svg`;
    default:
      return `${baseUrl}/card-generic.svg`;
  }
};

const PaymentMethodCard = ({
  method,
  onSetDefault,
  onDelete,
}: {
  method: PaymentMethod;
  onSetDefault: () => void;
  onDelete: () => void;
}) => (
  <Card className="relative">
    {method.isDefault && (
      <div className="absolute top-0 left-1/2 -translate-y-1/2 -translate-x-1/2">
        <Badge variant="outline" className="text-xs font-normal bg-white">
          Default
        </Badge>
      </div>
    )}
    <CardContent className="p-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img
            src={getCardBrandLogo(method.brand)}
            alt={method.brand ?? "Card"}
            className="h-8 w-auto"
          />
          <div className="flex flex-col">
            <span className="font-medium">•••• {method.last4}</span>
            <p className="text-sm text-muted-foreground">
              Expires {method.expiryMonth}/{method.expiryYear}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!method.isDefault && (
            <Button variant="outline" size="sm" onClick={onSetDefault}>
              Make Default
            </Button>
          )}
          <Button variant="destructive" size="sm" onClick={onDelete}>
            Remove
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function PaymentMethodScreen() {
  const { customer, isLoading: isSessionLoading } = useSession();
  const { toast } = useToast();
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);

  const { data: billingData, isLoading, error } = usePaymentMethods();

  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Error loading payment methods",
        description: "Please try again later",
      });
    }
  }, [error, toast]);

  const setDefaultPaymentMethodMutation = useMakePaymentMethodDefault({
    onSuccess: () => {
      toast({
        title: "Default payment method updated",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error updating default payment method",
      });
    },
  });

  const deletePaymentMethodMutation = useDeletePaymentMethod({
    onSuccess: () => {
      toast({
        title: "Payment method removed",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error removing payment method",
      });
    },
  });

  if (isSessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!customer?.id) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">
          Please log in to manage payment methods
        </p>
      </div>
    );
  }

  return (
    <div className="container max-w-xl space-y-8">
      <div>
        <h2 className="text-2xl font-semibold">Payment Methods</h2>
        <p className="text-sm text-muted-foreground">
          Manage your payment methods and billing preferences
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Payment Style</h3>
        </div>
        <div className="p-4 border rounded-md bg-muted/10 space-y-2">
          <p className="font-medium">
            {
              getPaymentStyleInfo(
                billingData?.paymentStyle ?? PaymentStyle.MANUAL
              ).label
            }
          </p>
          <p className="text-sm text-muted-foreground">
            {
              getPaymentStyleInfo(
                billingData?.paymentStyle ?? PaymentStyle.MANUAL
              ).description
            }
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Your Payment Methods</h2>
          <Button onClick={() => setIsAddPaymentMethodOpen(true)}>
            Add Payment Method
          </Button>
        </div>
        {isLoading ? (
          <Card>
            <CardContent className="p-6 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
            </CardContent>
          </Card>
        ) : !billingData?.paymentMethods?.length ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">
                No payment methods added yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          billingData.paymentMethods
            .slice()
            .sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : 0))
            .map((method: PaymentMethod) => (
              <PaymentMethodCard
                key={method.id}
                method={method}
                onSetDefault={() =>
                  setDefaultPaymentMethodMutation.mutate(method.id)
                }
                onDelete={() => deletePaymentMethodMutation.mutate(method.id)}
              />
            ))
        )}
      </div>

      <AddPaymentMethodDialog
        open={isAddPaymentMethodOpen}
        onOpenChange={setIsAddPaymentMethodOpen}
      />
    </div>
  );
}
